export type CarrierOtaAction='check'|'download'|'activate'|'rollback';
export type CarrierOtaPhase='unavailable'|'idle'|'checking'|'available'|'downloading'|'staged'|'activating'|'rolling-back'|'up-to-date'|'failed';

export interface CarrierOtaUpdateView{
  readonly releaseId:string;
  readonly runtimeVersion:string;
  readonly channel:string;
  readonly minCarrierVersionCode:number;
  readonly bridgeVersion:number;
}

export interface CarrierOtaSnapshot{
  readonly bridgeAvailable:boolean;
  readonly phase:CarrierOtaPhase;
  readonly pendingAction:CarrierOtaAction|null;
  readonly pendingRequestId:string|null;
  readonly latestUpdate:CarrierOtaUpdateView|null;
  readonly errorCode:string|null;
  readonly lastResponseType:string|null;
}

export interface CarrierOtaMessageTarget{
  addEventListener(type:'message',listener:(event:{data:unknown})=>void):void;
  removeEventListener(type:'message',listener:(event:{data:unknown})=>void):void;
}

export interface CarrierOtaNativeBridge extends CarrierOtaMessageTarget{
  postMessage(message:string):void;
}

export interface CarrierOtaPort{
  readonly subscribe:(listener:()=>void)=>()=>void;
  readonly getSnapshot:()=>CarrierOtaSnapshot;
  readonly check:()=>string|null;
  readonly download:()=>string|null;
  readonly activate:()=>string|null;
  readonly rollback:()=>string|null;
  readonly close:()=>void;
}

interface CarrierOtaPortOptions{
  readonly bridge?:CarrierOtaNativeBridge;
  readonly windowTarget?:CarrierOtaMessageTarget;
  readonly nextRequestId?:()=>string;
}

interface ParsedCarrierMessage{
  readonly type:string;
  readonly requestId?:string;
  readonly status?:string;
  readonly state?:string;
  readonly errorCode?:string;
  readonly update?:unknown;
}

const allowedCompletionTypes=new Set([
  'runtime.update.check.completed',
  'runtime.update.download.completed',
]);

const allowedImmediateTypes=new Set([
  'runtime.update.check.result',
  'runtime.update.download.result',
  'runtime.update.activate.result',
  'runtime.rollback.result',
  'carrier.error',
]);

const initialSnapshot=(available:boolean):CarrierOtaSnapshot=>({
  bridgeAvailable:available,
  phase:available?'idle':'unavailable',
  pendingAction:null,
  pendingRequestId:null,
  latestUpdate:null,
  errorCode:null,
  lastResponseType:null,
});

const normalizeUpdate=(value:unknown):CarrierOtaUpdateView|null=>{
  if(!value||typeof value!=='object')return null;
  const record=value as Record<string,unknown>;
  if(typeof record.releaseId!=='string'||!record.releaseId.trim())return null;
  if(typeof record.runtimeVersion!=='string'||!record.runtimeVersion.trim())return null;
  if(typeof record.channel!=='string'||!record.channel.trim())return null;
  if(typeof record.minCarrierVersionCode!=='number'||!Number.isInteger(record.minCarrierVersionCode)||record.minCarrierVersionCode<=0)return null;
  if(typeof record.bridgeVersion!=='number'||!Number.isInteger(record.bridgeVersion)||record.bridgeVersion<=0)return null;
  return {
    releaseId:record.releaseId,
    runtimeVersion:record.runtimeVersion,
    channel:record.channel,
    minCarrierVersionCode:record.minCarrierVersionCode,
    bridgeVersion:record.bridgeVersion,
  };
};

const parseMessage=(data:unknown):ParsedCarrierMessage|null=>{
  if(typeof data!=='string'||!data.trim())return null;
  try{
    const value=JSON.parse(data) as unknown;
    if(!value||typeof value!=='object')return null;
    const record=value as Record<string,unknown>;
    if(typeof record.type!=='string'||!record.type.trim())return null;
    return {
      type:record.type,
      requestId:typeof record.requestId==='string'&&record.requestId.trim()?record.requestId:undefined,
      status:typeof record.status==='string'?record.status:undefined,
      state:typeof record.state==='string'?record.state:undefined,
      errorCode:typeof record.errorCode==='string'?record.errorCode:undefined,
      update:record.update,
    };
  }catch{return null;}
};

const phaseForCheckState=(state:string|undefined):CarrierOtaPhase|null=>{
  if(state==='available')return 'available';
  if(state==='staged')return 'staged';
  if(state==='up-to-date')return 'up-to-date';
  if(state==='failed')return 'failed';
  if(state==='checking')return 'checking';
  return null;
};

const actionForType=(type:string):CarrierOtaAction|null=>{
  if(type.startsWith('runtime.update.check'))return 'check';
  if(type.startsWith('runtime.update.download'))return 'download';
  if(type==='runtime.update.activate.result')return 'activate';
  if(type==='runtime.rollback.result')return 'rollback';
  return null;
};

export function createCarrierOtaPort(options:CarrierOtaPortOptions={}):CarrierOtaPort{
  const browserWindow=typeof window!=='undefined'?window:undefined;
  const nativeWindow=browserWindow as (Window&{moreFunNative?:CarrierOtaNativeBridge})|undefined;
  const bridge=options.bridge??nativeWindow?.moreFunNative;
  const windowTarget=options.windowTarget??(browserWindow as unknown as CarrierOtaMessageTarget|undefined);
  let sequence=0;
  const nextRequestId=options.nextRequestId??(()=>`ota-${Date.now().toString(36)}-${++sequence}`);
  let snapshot=initialSnapshot(Boolean(bridge));
  const listeners=new Set<()=>void>();
  let closed=false;

  const publish=(next:CarrierOtaSnapshot)=>{
    if(closed)return;
    snapshot=next;
    for(const listener of listeners)listener();
  };

  const clearPending=(next:Omit<CarrierOtaSnapshot,'bridgeAvailable'>)=>publish({bridgeAvailable:Boolean(bridge),...next});

  const onMessage=(event:{data:unknown})=>{
    const message=parseMessage(event.data);
    if(!message)return;
    if(!allowedCompletionTypes.has(message.type)&&!allowedImmediateTypes.has(message.type))return;

    const expectedAction=actionForType(message.type);
    // A response carrying a requestId belongs to this presentation port only when it
    // matches the one explicit B action currently in flight. This prevents stale or
    // unrelated Native capability errors from becoming fake OTA truth in B.
    if(message.requestId&&message.requestId!==snapshot.pendingRequestId)return;
    if(!message.requestId&&snapshot.pendingAction&&expectedAction&&snapshot.pendingAction!==expectedAction)return;

    const update=normalizeUpdate(message.update)??snapshot.latestUpdate;
    if(message.type==='carrier.error'){
      // Generic Native errors without our request identity may belong to printers,
      // USB or another capability and must never be reclassified as OTA failure.
      if(!message.requestId||!snapshot.pendingRequestId)return;
      clearPending({
        phase:'failed',pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:message.errorCode??'NATIVE_CAPABILITY_FAILED',lastResponseType:message.type,
      });
      return;
    }

    if(message.status==='failed'||message.state==='failed'){
      clearPending({
        phase:'failed',pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:message.errorCode??'RUNTIME_UPDATE_FAILED',lastResponseType:message.type,
      });
      return;
    }

    if(message.type==='runtime.update.check.result'){
      publish({...snapshot,phase:'checking',errorCode:null,lastResponseType:message.type});
      return;
    }
    if(message.type==='runtime.update.download.result'){
      publish({...snapshot,phase:'downloading',errorCode:null,lastResponseType:message.type});
      return;
    }
    if(message.type==='runtime.update.activate.result'){
      clearPending({
        phase:'activating',pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:null,lastResponseType:message.type,
      });
      return;
    }
    if(message.type==='runtime.rollback.result'){
      clearPending({
        phase:'rolling-back',pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:null,lastResponseType:message.type,
      });
      return;
    }
    if(message.type==='runtime.update.check.completed'){
      const phase=phaseForCheckState(message.state);
      if(!phase)return;
      clearPending({
        phase,pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:phase==='failed'?(message.errorCode??'RUNTIME_UPDATE_CHECK_FAILED'):null,lastResponseType:message.type,
      });
      return;
    }
    if(message.type==='runtime.update.download.completed'){
      if(message.state!=='staged')return;
      clearPending({
        phase:'staged',pendingAction:null,pendingRequestId:null,latestUpdate:update,
        errorCode:null,lastResponseType:message.type,
      });
    }
  };

  bridge?.addEventListener?.('message',onMessage);
  windowTarget?.addEventListener?.('message',onMessage);

  const send=(action:CarrierOtaAction,type:string,phase:CarrierOtaPhase):string|null=>{
    if(!bridge||closed||snapshot.pendingAction)return null;
    if(action==='download'&&snapshot.phase!=='available')return null;
    if(action==='activate'&&snapshot.phase!=='staged')return null;
    const requestId=nextRequestId();
    publish({...snapshot,phase,pendingAction:action,pendingRequestId:requestId,errorCode:null,lastResponseType:null});
    try{
      bridge.postMessage(JSON.stringify({type,requestId}));
      return requestId;
    }catch(error){
      publish({...snapshot,phase:'failed',pendingAction:null,pendingRequestId:null,errorCode:error instanceof Error&&error.message?error.message:'NATIVE_BRIDGE_POST_FAILED',lastResponseType:'carrier.error'});
      return null;
    }
  };

  return {
    subscribe(listener){listeners.add(listener);return()=>listeners.delete(listener);},
    getSnapshot(){return snapshot;},
    check(){return send('check','runtime.update.check','checking');},
    download(){return send('download','runtime.update.download','downloading');},
    activate(){return send('activate','runtime.update.activate','activating');},
    rollback(){return send('rollback','runtime.rollback','rolling-back');},
    close(){
      if(closed)return;
      closed=true;
      bridge?.removeEventListener?.('message',onMessage);
      windowTarget?.removeEventListener?.('message',onMessage);
      listeners.clear();
    },
  };
}
